import React, { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";

const text = (value) => String(value ?? "").trim();

const CARGO_LABELS = {
  GENERAL: "一般貨物",
  STEEL_PLATE: "鋼板",
  STEEL_COIL: "鋼捲",
  VEHICLE: "汽車／車輛",
  EXCAVATOR: "挖土機",
  HEAVY_MACHINERY: "重型機械",
  BUILDING_MATERIAL: "建材",
  AGRICULTURAL: "農產品",
  FOOD: "食品",
  OTHER: "其他",
};

const LEGAL_LABELS = {
  HEAVY_TRUCK: "大貨車",
  TRACTOR: "曳引車",
  SEMI_TRAILER: "半拖車",
};

const TEMP_LABELS = {
  NONE: "無溫度需求",
  AMBIENT: "常溫",
  CHILLED: "冷藏",
  FROZEN: "冷凍",
};

function formatKg(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toLocaleString()} kg` : "未設定";
}

function formatCbm(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toLocaleString(undefined, { maximumFractionDigits: 2 })} cbm` : "未設定";
}

function legalTypeOf(item) {
  const explicit = text(item?.legalType).toUpperCase();
  if (LEGAL_LABELS[explicit]) return explicit;
  if (text(item?.assetType).toLowerCase() === "trailer") return "SEMI_TRAILER";
  const label = `${text(item?.vehicleType)} ${text(item?.bodyStyle)}`;
  if (label.includes("曳引") || label.toUpperCase().includes("TRACTOR")) return "TRACTOR";
  if (label.includes("半拖") || label.toUpperCase().includes("SEMI")) return "SEMI_TRAILER";
  return "HEAVY_TRUCK";
}

function normalizeSummaryItems(data) {
  const source = data?.items;
  const values = Array.isArray(source)
    ? source.map((item) => ({ ...item, vehicleId: item?.vehicleId || item?.id }))
    : Object.entries(source || {}).map(([vehicleId, item]) => ({ ...item, vehicleId }));
  return values
    .map((item) => {
      const legalType = legalTypeOf(item);
      return {
        ...item,
        vehicleId: text(item.vehicleId),
        legalType,
        plateNumber: text(item.plateNumber) || "未填車牌",
        vehicleType: text(item.vehicleType) || LEGAL_LABELS[legalType],
        make: text(item.make || item.brand),
        maxLoadKg: item.maxLoadKg ?? item.tonnageKg ?? null,
        maxVolumeCbm: item.maxVolumeCbm ?? item.capacity?.maxVolumeCbm ?? null,
        palletCapacity: item.palletCapacity ?? item.capacity?.palletCapacity ?? null,
        maxGrossWeightKg: item.maxGrossWeightKg ?? null,
        bodyStyle: text(item.bodyStyle),
        temperatureType: text(item.temperatureType).toUpperCase() || "NONE",
        status: text(item.status).toLowerCase() || "active",
      };
    })
    .filter((item) => item.vehicleId);
}

function typeLabel(value) {
  return CARGO_LABELS[text(value).toUpperCase()] || text(value) || "未指定";
}

function temperatureLabel(value) {
  return TEMP_LABELS[text(value).toUpperCase()] || text(value) || "未指定";
}

function numberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function requiredTemperature(row) {
  return text(row?.temperatureType).toUpperCase() || "NONE";
}

function temperatureCompatible(rowTemperature, assetTemperature) {
  if (rowTemperature === "NONE") return true;
  if (rowTemperature === "AMBIENT") return ["AMBIENT", "CHILLED", "FROZEN", "NONE"].includes(assetTemperature);
  return assetTemperature === rowTemperature;
}

function getAssetCompatibility(rows, asset) {
  const compatible = [];
  const incompatible = [];
  const assetTemperature = text(asset.temperatureType).toUpperCase() || "NONE";
  rows.forEach((row, index) => {
    const reasons = [];
    const weight = numberOrNull(row.weightKg);
    const volume = numberOrNull(row.volumeCbm);
    const palletCount = numberOrNull(row.palletCount);
    const maxLoadKg = numberOrNull(asset.maxLoadKg);
    const maxVolumeCbm = numberOrNull(asset.maxVolumeCbm);
    const palletCapacity = numberOrNull(asset.palletCapacity);
    if (weight !== null && maxLoadKg !== null && weight > maxLoadKg) reasons.push("重量超過");
    if (volume !== null && maxVolumeCbm !== null && volume > maxVolumeCbm) reasons.push("材積超過");
    if (palletCount !== null && palletCapacity !== null && palletCount > palletCapacity) reasons.push("棧板超過");
    if (!temperatureCompatible(requiredTemperature(row), assetTemperature)) reasons.push("溫度不符");
    const cargo = {
      row,
      rowNumber: row.rowNumber || index + 2,
      label: row.recipientName || row.cargoName || `第 ${row.rowNumber || index + 2} 列貨物`,
      type: typeLabel(row.cargoType),
      weightKg: weight,
    };
    if (reasons.length) incompatible.push({ ...cargo, reasons });
    else compatible.push(cargo);
  });
  return { compatible, incompatible, total: rows.length };
}

function initialAssetIds(initialData) {
  const oldUnits = initialData?.selectedAssets || initialData?.selectedTransportUnits || [];
  return new Set(oldUnits.flatMap((unit) => {
    if (unit.unitType === "RIGID_TRUCK") return unit.vehicleId ? [`truck:${unit.vehicleId}`] : [];
    if (unit.unitType === "TRAILER" || unit.unitType === "SEMI_TRAILER") {
      const id = unit.trailerId || unit.assetId || unit.vehicleId;
      return id ? [`trailer:${id}`] : [];
    }
    return [];
  }));
}

function Icon({ name, size = 18 }) {
  const paths = {
    arrow: "M5 12h14m-6-6 6 6-6 6",
    back: "M19 12H5m6-6-6 6 6 6",
    truck: "M2 5h12v12H2zM14 9h4l4 4v4h-8M8 18a2 2 0 1 1-4 0M20 18a2 2 0 1 1-4 0",
    trailer: "M3 7h15v9H3zM18 11h3l2 2v3h-5M7 18a2 2 0 1 1-4 0M20 18a2 2 0 1 1-4 0",
    check: "m5 12 4 4L19 6",
    alert: "M12 4 21 20H3L12 4Zm0 5v5m0 3h.01",
    sparkle: "m12 3 1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7L12 3Z",
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={paths[name] || paths.truck} /></svg>;
}

function AssetStats({ item }) {
  return (
    <div className="qfv-asset-stats">
      <span><b>核定載重</b>{formatKg(item.maxLoadKg)}</span>
      <span><b>核定總重</b>{formatKg(item.maxGrossWeightKg)}</span>
      <span><b>材積</b>{formatCbm(item.maxVolumeCbm)}</span>
      <span><b>溫度</b>{temperatureLabel(item.temperatureType)}</span>
    </div>
  );
}

function AssetRow({ title, subtitle, items, selectedIds, onToggle, onSelectAll, icon, tone, compatibilityById }) {
  const allSelected = items.length > 0 && items.every((item) => selectedIds.has(item.vehicleId));
  const selectedCount = items.filter((item) => selectedIds.has(item.vehicleId)).length;
  return (
    <section className="qfv-asset-section">
      <div className="qfv-section-head">
        <div className="qfv-section-title-wrap">
          <span className={`qfv-section-icon ${tone}`}><Icon name={icon} size={18} /></span>
          <div><h2>{title}</h2><p>{subtitle}</p></div>
        </div>
        <div className="qfv-section-actions">
          <span className="qfv-section-count">{selectedCount}/{items.length} 已選</span>
          <button type="button" className={`qfv-select-all ${allSelected ? "is-active" : ""}`} onClick={onSelectAll} disabled={!items.length}>
            {allSelected ? "取消全選" : "全選"}
          </button>
        </div>
      </div>
      {items.length ? <div className="qfv-asset-strip">{items.map((item) => {
        const selected = selectedIds.has(item.vehicleId);
        const compatibility = compatibilityById[item.vehicleId] || { compatible: [], incompatible: [], total: 0 };
        const preview = compatibility.compatible.slice(0, 2).map((cargo) => cargo.label).join("、");
        return <label className={`qfv-asset-card ${selected ? "is-selected" : ""}`} key={item.vehicleId}>
          <input type="checkbox" checked={selected} onChange={() => onToggle(item.vehicleId)} />
          <span className={`qfv-asset-icon ${tone}`}><Icon name={icon} size={19} /></span>
          <span className="qfv-asset-main"><span className="qfv-asset-title-row"><strong>{item.plateNumber}</strong><span className="qfv-check"><Icon name="check" size={14} /></span></span><small>{item.make ? `${item.make} · ` : ""}{item.vehicleType}</small><span className="qfv-asset-body">{item.bodyStyle || "車身式樣未設定"}</span><span className={`qfv-fit-summary ${compatibility.compatible.length ? "is-ok" : "is-blocked"}`}><Icon name={compatibility.compatible.length ? "check" : "alert"} size={11} />可接 {compatibility.compatible.length}/{compatibility.total} 筆貨物</span><span className="qfv-fit-preview">{preview || "目前沒有符合的貨物"}{compatibility.compatible.length > 2 ? ` 等 ${compatibility.compatible.length} 筆` : ""}</span></span>
          <AssetStats item={item} />
        </label>;
      })}</div> : <div className="qfv-no-assets">目前沒有可用的{title}。</div>}
    </section>
  );
}

export default function QuickCreateFleetVehicleStep({ user, account, initialData, jobDate, rows = [], onNext, onBack }) {
  const [authUser, setAuthUser] = useState(user || auth.currentUser || null);
  const [authAccount, setAuthAccount] = useState(account || null);
  const [authChecked, setAuthChecked] = useState(Boolean(user?.uid && account?.orgId));
  const effectiveUser = user || authUser;
  const effectiveAccount = account?.orgId ? account : authAccount;
  const orgId = text(effectiveAccount?.orgId || initialData?.orgId);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const initialIds = useMemo(() => initialAssetIds(initialData), [initialData]);
  const [selectedTruckIds, setSelectedTruckIds] = useState(() => new Set([...initialIds].filter((key) => key.startsWith("truck:")).map((key) => key.slice(6))));
  const [selectedTrailerIds, setSelectedTrailerIds] = useState(() => new Set([...initialIds].filter((key) => key.startsWith("trailer:")).map((key) => key.slice(8))));
  const [confirmed, setConfirmed] = useState(() => initialIds.size > 0);

  useEffect(() => {
    let alive = true;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!alive) return;
      setAuthUser(firebaseUser || null);
      if (!firebaseUser?.uid) {
        setAuthAccount(null);
        setAuthChecked(true);
        return;
      }
      if (account?.orgId) {
        setAuthChecked(true);
        return;
      }
      try {
        const snapshot = await getDoc(doc(db, "drivers", firebaseUser.uid));
        if (alive) setAuthAccount(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
      } catch (accountError) {
        console.error("[QuickCreateFleetVehicleStep] account load:", accountError);
        if (alive) setAuthAccount(null);
      } finally {
        if (alive) setAuthChecked(true);
      }
    });
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [account?.orgId]);

  useEffect(() => {
    let alive = true;
    async function loadSummary() {
      if (!authChecked) return;
      if (!orgId) {
        setLoading(false);
        setError("目前登入帳號尚未取得 orgId，無法讀取車輛列表。");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const snapshot = await getDoc(doc(db, "orgs", orgId, "vehicleSummary", "list"));
        if (!alive) return;
        setItems(snapshot.exists() ? normalizeSummaryItems(snapshot.data()) : []);
      } catch (loadError) {
        console.error("[QuickCreateFleetVehicleStep] summary load:", loadError);
        if (alive) setError("車輛列表讀取失敗，請稍後再試。");
      } finally {
        if (alive) setLoading(false);
      }
    }
    loadSummary();
    return () => {
      alive = false;
    };
  }, [authChecked, orgId]);

  const activeItems = useMemo(() => items.filter((item) => item.status !== "inactive"), [items]);
  const trucks = useMemo(() => activeItems.filter((item) => item.legalType === "HEAVY_TRUCK"), [activeItems]);
  const trailers = useMemo(() => activeItems.filter((item) => item.legalType === "SEMI_TRAILER"), [activeItems]);
  const availableAssetCount = trucks.length + trailers.length;

  const cargoSummary = useMemo(() => {
    const byType = new Map();
    let totalWeight = 0;
    rows.forEach((row) => {
      const key = text(row.cargoType).toUpperCase() || "OTHER";
      byType.set(key, (byType.get(key) || 0) + 1);
      const weight = Number(row.weightKg);
      if (Number.isFinite(weight)) totalWeight += weight;
    });
    return { byType: [...byType.entries()], totalWeight };
  }, [rows]);

  const compatibilityById = useMemo(() => Object.fromEntries(activeItems.map((item) => [item.vehicleId, getAssetCompatibility(rows, item)])), [activeItems, rows]);

  const selectedAssets = useMemo(() => {
    const truckAssets = trucks.filter((truck) => selectedTruckIds.has(truck.vehicleId)).map((truck) => ({
      assetId: truck.vehicleId,
      assetRole: "VEHICLE",
      unitType: "RIGID_TRUCK",
      vehicleId: truck.vehicleId,
      trailerId: null,
      asset: truck,
      usablePayloadKg: truck.maxLoadKg,
      maxVolumeCbm: truck.maxVolumeCbm,
      palletCapacity: truck.palletCapacity,
      temperatureType: truck.temperatureType,
      bodyStyle: truck.bodyStyle,
    }));
    const trailerAssets = trailers.filter((trailer) => selectedTrailerIds.has(trailer.vehicleId)).map((trailer) => ({
      assetId: trailer.vehicleId,
      assetRole: "TRAILER",
      unitType: "TRAILER",
      vehicleId: null,
      trailerId: trailer.vehicleId,
      asset: trailer,
      usablePayloadKg: trailer.maxLoadKg,
      maxVolumeCbm: trailer.maxVolumeCbm,
      palletCapacity: trailer.palletCapacity,
      temperatureType: trailer.temperatureType,
      bodyStyle: trailer.bodyStyle,
    }));
    return [...truckAssets, ...trailerAssets];
  }, [selectedTrailerIds, selectedTruckIds, trailers, trucks]);

  function toggleTruck(vehicleId) {
    setConfirmed(false);
    setSelectedTruckIds((current) => {
      const next = new Set(current);
      if (next.has(vehicleId)) next.delete(vehicleId);
      else next.add(vehicleId);
      return next;
    });
  }

  function toggleTrailer(vehicleId) {
    setConfirmed(false);
    setSelectedTrailerIds((current) => {
      const next = new Set(current);
      if (next.has(vehicleId)) next.delete(vehicleId);
      else next.add(vehicleId);
      return next;
    });
  }

  function toggleAll(itemsToSelect, setter) {
    setConfirmed(false);
    setter((current) => {
      const allSelected = itemsToSelect.length > 0 && itemsToSelect.every((item) => current.has(item.vehicleId));
      return allSelected ? new Set() : new Set(itemsToSelect.map((item) => item.vehicleId));
    });
  }

  function clearAll() {
    setConfirmed(false);
    setSelectedTruckIds(new Set());
    setSelectedTrailerIds(new Set());
  }

  function confirmAssets() {
    if (!selectedAssets.length) {
      setError("請至少選擇一台大貨車或一台拖車。");
      return;
    }
    const assetCompatibility = Object.fromEntries(selectedAssets.map((asset) => {
      const fit = compatibilityById[asset.assetId] || { compatible: [], incompatible: [] };
      return [asset.assetId, {
        compatibleRowNumbers: fit.compatible.map((cargo) => cargo.rowNumber),
        incompatible: fit.incompatible.map((cargo) => ({ rowNumber: cargo.rowNumber, reasons: cargo.reasons })),
      }];
    }));
    setError("");
    setConfirmed(true);
    onNext?.({ jobDate, rows, selectedAssets, assetCompatibility });
  }

  if (!authChecked) return <div className="qfv-center"><span className="qfv-spinner" />驗證登入與組織資料中…<style>{styles}</style></div>;
  if (!effectiveUser?.uid) return <div className="qfv-center"><strong>選擇承載資產</strong><span>請先登入帳號。</span><style>{styles}</style></div>;

  return (
    <div className="qfv-page">
      <header className="qfv-header">
        <div><div className="qfv-eyebrow">QUICK CREATE · STEP 2</div><h1>選擇承載資產</h1><p>先確認這批貨物要使用哪些大貨車與拖車，再進入分群。</p></div>
        <button type="button" className="qfv-secondary" onClick={onBack}><Icon name="back" size={16} />返回資料預檢</button>
      </header>

      <div className="qfv-progress"><span className="is-done"><b>01</b>資料與定位</span><i>›</i><span className="is-active"><b>02</b>承載資產</span><i>›</i><span><b>03</b>分群預覽</span></div>

      <section className="qfv-batch-summary"><div className="qfv-summary-lead"><span className="qfv-summary-kicker">這批貨件</span><strong>{rows.length} 筆配送資料</strong><span>工作日期：{jobDate || "尚未設定"}</span></div><div className="qfv-summary-total"><strong>{formatKg(cargoSummary.totalWeight)}</strong><span>貨物總重量</span></div><div className="qfv-summary-assets"><span><b>{trucks.length}</b> 台大貨車</span><span><b>{trailers.length}</b> 台拖車</span><span><b>{selectedAssets.length}</b> 個已選資產</span></div><div className="qfv-cargo-types">{cargoSummary.byType.map(([key, count]) => <span key={key}>{typeLabel(key)} <b>{count}</b></span>)}</div></section>

      <div className="qfv-selection-toolbar"><div><strong>選擇承載資產</strong><span>可以多選；全選只作用於目前的資產類別。</span></div><button type="button" className="qfv-clear-button" onClick={clearAll} disabled={!selectedAssets.length}>清除全部選擇</button></div>

      <section className="qfv-live-compatibility"><div className="qfv-live-head"><div><strong>目前選擇可接貨物</strong><span>勾選或取消車輛後立即更新；先依單筆重量、材積、棧板與溫度判斷。</span></div><b>{selectedAssets.length} 個資產</b></div>{selectedAssets.length ? <div className="qfv-live-grid">{selectedAssets.map((asset) => { const fit = compatibilityById[asset.assetId] || { compatible: [], incompatible: [], total: rows.length }; return <div className="qfv-live-card" key={asset.assetId}><div className="qfv-live-title"><span className={asset.assetRole === "TRAILER" ? "is-purple" : "is-orange"}><Icon name={asset.assetRole === "TRAILER" ? "trailer" : "truck"} size={14} /></span><strong>{asset.asset?.plateNumber || asset.assetId}</strong><em>{asset.assetRole === "TRAILER" ? "拖車" : "大貨車"}</em></div><div className="qfv-live-count"><b>{fit.compatible.length}</b><span>/ {fit.total} 筆貨物可接</span></div><p>{fit.compatible.slice(0, 4).map((cargo) => cargo.label).join("、") || "目前沒有符合的貨物"}{fit.compatible.length > 4 ? ` 等 ${fit.compatible.length} 筆` : ""}</p></div>; })}</div> : <div className="qfv-live-empty">先勾選大貨車或拖車，就會在這裡看到它目前可以承接的貨物。</div>}</section>

      {error ? <div className="qfv-message qfv-error"><Icon name="alert" size={17} />{error}</div> : null}
      {loading ? <div className="qfv-center"><span className="qfv-spinner" />讀取摘要車輛列表中…</div> : null}
      {!loading && !error && !availableAssetCount ? <div className="qfv-empty"><Icon name="truck" size={30} /><strong>目前沒有可用承載資產</strong><span>請先到車輛管理新增並啟用大貨車或半拖車。</span></div> : null}

      {!loading && availableAssetCount ? <div className="qfv-asset-sections">
        <AssetRow title="大貨車" subtitle="一體式車輛，可直接作為承載資產" items={trucks} selectedIds={selectedTruckIds} onToggle={toggleTruck} onSelectAll={() => toggleAll(trucks, setSelectedTruckIds)} icon="truck" tone="orange" compatibilityById={compatibilityById} />
        <AssetRow title="拖車" subtitle="容量、車身與溫度能力以拖車本身資料為主" items={trailers} selectedIds={selectedTrailerIds} onToggle={toggleTrailer} onSelectAll={() => toggleAll(trailers, setSelectedTrailerIds)} icon="trailer" tone="purple" compatibilityById={compatibilityById} />
      </div> : null}

      <footer className="qfv-footer"><span><Icon name="check" size={15} />{confirmed ? `已確認 ${selectedAssets.length} 個承載資產；${rows.length} 筆貨件已完整保留。` : `已選擇 ${selectedAssets.length} 個承載資產`}</span><button type="button" className="qfv-primary" onClick={confirmAssets} disabled={loading || !rows.length}>{confirmed ? <><Icon name="check" size={16} />已確認資產</> : <><Icon name="arrow" size={16} />確認資產，準備分群</>}</button></footer>
      <style>{styles}</style>
    </div>
  );
}

const styles = `
.qfv-page{min-height:100%;box-sizing:border-box;padding:32px 38px 56px;background:#F5F5F7;color:#1D1D1F;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Segoe UI",sans-serif;overflow-y:auto;-webkit-overflow-scrolling:touch}.qfv-header{max-width:1440px;margin:0 auto 18px;display:flex;align-items:flex-end;justify-content:space-between;gap:24px}.qfv-eyebrow{color:#8E8E93;font-size:10px;font-weight:800;letter-spacing:1.8px}.qfv-header h1{margin:7px 0 6px;color:#1D1D1F;font-size:31px;line-height:1.12;letter-spacing:-.9px;font-weight:720}.qfv-header p{margin:0;color:#86868B;font-size:13px}.qfv-primary,.qfv-secondary,.qfv-select-all,.qfv-clear-button{border:0;font:inherit;cursor:pointer;box-sizing:border-box}.qfv-primary,.qfv-secondary{min-height:40px;padding:0 15px;display:inline-flex;align-items:center;justify-content:center;gap:7px;border-radius:10px;font-size:12px;font-weight:700}.qfv-primary{border:1px solid #F18432;background:#F18432;color:#FFF;box-shadow:0 8px 18px rgba(241,132,50,.18)}.qfv-primary:hover:not(:disabled){background:#E87321}.qfv-primary:disabled{cursor:not-allowed;opacity:.45;box-shadow:none}.qfv-secondary{border:1px solid #DCDCE1;background:rgba(255,255,255,.8);color:#4B4B50}.qfv-secondary:hover{border-color:#B9B9BF;background:#FFF}.qfv-progress,.qfv-batch-summary,.qfv-selection-toolbar,.qfv-live-compatibility,.qfv-message,.qfv-asset-sections,.qfv-footer{max-width:1440px;margin-left:auto;margin-right:auto}.qfv-progress{display:flex;align-items:center;gap:9px;margin-bottom:14px;color:#A1A1A6;font-size:10px}.qfv-progress span{display:inline-flex;align-items:center;gap:6px;padding:6px 9px;border-radius:99px;background:#EBEBED}.qfv-progress span b{font-size:9px}.qfv-progress span.is-done{background:#E8F7EE;color:#23844D}.qfv-progress span.is-active{background:#FFF0E5;color:#D66E22}.qfv-progress i{font-style:normal;color:#B4B4B8;font-size:16px}.qfv-batch-summary{padding:18px 20px;display:grid;grid-template-columns:minmax(180px,1.1fr) minmax(150px,.8fr) minmax(215px,1fr) minmax(280px,2fr);align-items:center;gap:18px;border:1px solid #E4E4E8;border-radius:17px;background:rgba(255,255,255,.86);box-shadow:0 4px 18px rgba(0,0,0,.025)}.qfv-summary-lead,.qfv-summary-total{display:grid;gap:4px}.qfv-summary-kicker{color:#8E8E93;font-size:10px}.qfv-summary-lead strong{color:#1D1D1F;font-size:18px;letter-spacing:-.3px}.qfv-summary-lead>span:last-child,.qfv-summary-total span{color:#8E8E93;font-size:10px}.qfv-summary-total{padding-left:18px;border-left:1px solid #E6E6EA}.qfv-summary-total strong{color:#1D1D1F;font-size:22px;letter-spacing:-.5px}.qfv-summary-assets{display:flex;flex-wrap:wrap;gap:7px}.qfv-summary-assets span{padding:7px 9px;border-radius:8px;background:#F2F2F5;color:#6E6E73;font-size:10px}.qfv-summary-assets b{color:#1D1D1F;font-size:12px}.qfv-cargo-types{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:6px}.qfv-cargo-types span{padding:6px 8px;border-radius:7px;background:#F7F7F8;color:#6E6E73;font-size:9px;white-space:nowrap}.qfv-cargo-types b{color:#1D1D1F}.qfv-selection-toolbar{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-top:27px;margin-bottom:12px}.qfv-selection-toolbar strong{display:block;color:#1D1D1F;font-size:18px;letter-spacing:-.3px}.qfv-selection-toolbar span{display:block;margin-top:4px;color:#8E8E93;font-size:11px}.qfv-clear-button{padding:7px 10px;border-radius:8px;background:transparent;color:#8E8E93;font-size:10px}.qfv-clear-button:hover:not(:disabled){background:#EAEAED;color:#4B4B50}.qfv-clear-button:disabled{cursor:not-allowed;opacity:.45}.qfv-live-compatibility{margin-bottom:13px;padding:13px 15px;border:1px solid #E4E4E8;border-radius:13px;background:rgba(255,255,255,.78)}.qfv-live-head{display:flex;align-items:center;justify-content:space-between;gap:12px}.qfv-live-head strong{display:block;color:#1D1D1F;font-size:13px}.qfv-live-head span{display:block;margin-top:3px;color:#8E8E93;font-size:9px}.qfv-live-head>b{padding:5px 8px;border-radius:7px;background:#F2F2F5;color:#6E6E73;font-size:9px;white-space:nowrap}.qfv-live-grid{display:flex;gap:8px;overflow-x:auto;margin-top:10px;padding-bottom:3px}.qfv-live-card{min-width:205px;flex:0 0 205px;padding:10px 11px;border:1px solid #E8E8EC;border-radius:10px;background:#FAFAFB}.qfv-live-title{display:flex;align-items:center;gap:6px;min-width:0}.qfv-live-title>span{width:24px;height:24px;display:grid;place-items:center;border-radius:7px}.qfv-live-title>span.is-orange{background:#FFF0E5;color:#E4772B}.qfv-live-title>span.is-purple{background:#F0EEFF;color:#6651BB}.qfv-live-title strong{min-width:0;overflow:hidden;color:#3F3F44;font-size:11px;text-overflow:ellipsis;white-space:nowrap}.qfv-live-title em{padding:3px 5px;border-radius:5px;background:#F2F2F5;color:#8E8E93;font-size:8px;font-style:normal;white-space:nowrap}.qfv-live-count{display:flex;align-items:baseline;gap:4px;margin-top:9px}.qfv-live-count b{color:#D66E22;font-size:19px}.qfv-live-count span{color:#6E6E73;font-size:9px}.qfv-live-card p{height:27px;overflow:hidden;margin:4px 0 0;color:#8E8E93;font-size:9px;line-height:1.5}.qfv-live-empty{margin-top:10px;padding:10px;border-radius:8px;background:#F7F7F8;color:#8E8E93;font-size:10px}.qfv-message{margin-bottom:12px;padding:11px 14px;display:flex;align-items:center;gap:8px;border-radius:10px;font-size:12px}.qfv-error{border:1px solid #F0CACA;background:#FFF4F4;color:#B84444}.qfv-asset-sections{display:grid;gap:13px}.qfv-asset-section{min-width:0;padding:16px 17px 15px;border:1px solid #E4E4E8;border-radius:16px;background:rgba(255,255,255,.9);box-shadow:0 3px 14px rgba(0,0,0,.02);overflow:hidden}.qfv-section-head{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:9px;position:relative;z-index:1}.qfv-section-title-wrap{display:flex;align-items:center;gap:10px}.qfv-section-icon,.qfv-asset-icon{display:grid;place-items:center;flex:0 0 auto}.qfv-section-icon{width:32px;height:32px;border-radius:10px}.qfv-section-icon.orange,.qfv-asset-icon.orange{background:#FFF0E5;color:#E4772B}.qfv-section-icon.purple,.qfv-asset-icon.purple{background:#F0EEFF;color:#6651BB}.qfv-section-head h2{margin:0;color:#1D1D1F;font-size:15px;letter-spacing:-.2px}.qfv-section-head p{margin:3px 0 0;color:#8E8E93;font-size:10px}.qfv-section-actions{display:flex;align-items:center;gap:8px}.qfv-section-count{color:#8E8E93;font-size:10px}.qfv-select-all{min-width:60px;padding:7px 10px;border:1px solid #DCDCE1;border-radius:8px;background:#FFF;color:#5B5B60;font-size:10px;font-weight:700}.qfv-select-all:hover:not(:disabled),.qfv-select-all.is-active{border-color:#F1A16A;background:#FFF7F0;color:#D66E22}.qfv-select-all:disabled{cursor:not-allowed;opacity:.45}.qfv-select-note{display:flex;align-items:center;gap:5px;margin:0 0 10px 42px;color:#A1A1A6;font-size:9px}.qfv-select-note svg{color:#B0B0B5}.qfv-asset-strip{width:100%;min-width:0;max-width:100%;display:flex;flex-wrap:nowrap;gap:10px;overflow-x:auto;overflow-y:hidden;padding:2px 2px 9px;scroll-snap-type:x proximity;scrollbar-width:thin}.qfv-asset-card{position:relative;min-width:306px;flex:0 0 306px;display:grid;grid-template-columns:38px minmax(120px,1fr) 150px;align-items:center;gap:11px;padding:13px;border:1px solid #E1E1E6;border-radius:13px;background:#FFF;cursor:pointer;scroll-snap-align:start;transition:border-color .18s ease,background .18s ease,box-shadow .18s ease,transform .18s ease}.qfv-asset-card:hover{border-color:#C7C7CC;transform:translateY(-1px)}.qfv-asset-card.is-selected{border-color:#F19A5D;background:#FFF9F4;box-shadow:0 0 0 2px rgba(241,132,50,.12)}.qfv-asset-card>input{position:absolute;opacity:0;pointer-events:none}.qfv-asset-icon{width:38px;height:38px;border-radius:11px}.qfv-asset-main{min-width:0}.qfv-asset-title-row{display:flex;align-items:center;gap:7px}.qfv-asset-title-row strong{min-width:0;overflow:hidden;color:#1D1D1F;font-size:13px;letter-spacing:-.1px;text-overflow:ellipsis;white-space:nowrap}.qfv-asset-main small{display:block;overflow:hidden;margin-top:3px;color:#6E6E73;font-size:9px;text-overflow:ellipsis;white-space:nowrap}.qfv-asset-body{display:block;overflow:hidden;margin-top:5px;color:#A1A1A6;font-size:9px;text-overflow:ellipsis;white-space:nowrap}.qfv-fit-summary{display:flex;align-items:center;gap:3px;margin-top:6px;font-size:9px;font-weight:700}.qfv-fit-summary.is-ok{color:#23844D}.qfv-fit-summary.is-blocked{color:#B84444}.qfv-fit-preview{display:block;overflow:hidden;margin-top:3px;color:#8E8E93;font-size:8px;text-overflow:ellipsis;white-space:nowrap}.qfv-check{width:20px;height:20px;display:grid;place-items:center;border:1px solid #D3D3D8;border-radius:50%;color:transparent}.qfv-asset-card.is-selected .qfv-check{border-color:#F18432;background:#F18432;color:#FFF}.qfv-asset-stats{display:grid;grid-template-columns:1fr 1fr;gap:5px 9px;padding-left:10px;border-left:1px solid #EEEEF0}.qfv-asset-stats span{display:grid;gap:2px;overflow:hidden;color:#4B4B50;font-size:9px;white-space:nowrap}.qfv-asset-stats b{color:#A1A1A6;font-size:8px;font-weight:600}.qfv-no-assets{padding:22px;border-radius:10px;background:#F7F7F8;color:#8E8E93;text-align:center;font-size:11px}.qfv-footer{margin-top:15px;padding:13px 16px;display:flex;align-items:center;justify-content:space-between;gap:14px;border:1px solid #E4E4E8;border-radius:13px;background:rgba(255,255,255,.9)}.qfv-footer>span{display:flex;align-items:center;gap:6px;color:#8E8E93;font-size:10px}.qfv-center{min-height:300px;display:grid;place-items:center;align-content:center;gap:9px;color:#7B7B80;font-size:12px}.qfv-spinner{width:18px;height:18px;border:2px solid #F4D6BF;border-top-color:#F18432;border-radius:50%;animation:qfvSpin .75s linear infinite}@keyframes qfvSpin{to{transform:rotate(360deg)}}.qfv-empty{min-height:240px;display:grid;place-items:center;align-content:center;gap:9px;color:#8E8E93;text-align:center}.qfv-empty strong{color:#4B4B50;font-size:16px}.qfv-empty span{font-size:11px}@media(max-width:1120px){.qfv-batch-summary{grid-template-columns:1fr 1fr}.qfv-cargo-types{justify-content:flex-start;grid-column:1 / -1}}@media(max-width:850px){.qfv-page{padding:23px 18px 42px}.qfv-header{display:block}.qfv-header .qfv-secondary{margin-top:14px}.qfv-progress{overflow-x:auto;white-space:nowrap}.qfv-summary-assets{align-self:start}.qfv-selection-toolbar{align-items:flex-end}.qfv-asset-card{min-width:286px;flex-basis:286px;grid-template-columns:36px minmax(115px,1fr) 132px}.qfv-footer{align-items:stretch;flex-direction:column}.qfv-footer .qfv-primary{width:100%}}@media(max-width:600px){.qfv-page{padding:18px 12px 30px}.qfv-header h1{font-size:25px}.qfv-batch-summary{grid-template-columns:1fr;padding:15px}.qfv-summary-total{padding:10px 0 0;border-left:0;border-top:1px solid #E6E6EA}.qfv-cargo-types{grid-column:auto}.qfv-selection-toolbar{display:block}.qfv-clear-button{margin-top:8px;padding-left:0}.qfv-section-head{align-items:flex-start}.qfv-section-actions{display:block;text-align:right}.qfv-section-count{display:block;margin-bottom:5px}.qfv-select-note{margin-left:0}.qfv-asset-card{min-width:285px;grid-template-columns:36px minmax(110px,1fr) 124px;padding:11px}.qfv-asset-stats{padding-left:8px;gap:4px}.qfv-asset-stats span{font-size:8px}.qfv-footer{padding:13px}}
`;
